import { predictRoasVector } from './roasSaturation';

/** MOB 12 in 1-indexed terms → zero-based index 11. */
export const UA_PREDICTION_TARGET_MOB = 11;
export const UA_PREDICTION_TARGET_ROAS = 1.0;

/** Zero-based MOB index for 100% ROAS headroom: latest available month, capped at MOB 12. */
export function resolveMaxRoas100TargetMob(hillModel) {
  const maxRoasByMob = hillModel?.maxRoasByMob;
  if (!maxRoasByMob?.length) {
    return null;
  }
  const lastAvailableMob = maxRoasByMob.length - 1;
  return Math.min(lastAvailableMob, UA_PREDICTION_TARGET_MOB);
}

export function predictHillRoasAtMob(hillModel, spend, mob) {
  const vector = predictRoasVector(hillModel, spend);
  if (!vector?.length || mob < 0 || mob >= vector.length) {
    return null;
  }
  const value = Number(vector[mob]);
  return Number.isFinite(value) ? value : null;
}

/**
 * Max marketing spend where Hill-predicted cumulative ROAS at `mob` stays at or above
 * `targetRoas` (1.0 = 100%).
 */
export function maxSpendForTargetRoasHill(hillModel, mob, targetRoas = UA_PREDICTION_TARGET_ROAS) {
  if (!hillModel?.k || !hillModel.maxRoasByMob?.length) {
    return null;
  }
  const k = Number(hillModel.k);
  const maxRoas = Number(hillModel.maxRoasByMob[mob]);
  if (!Number.isFinite(k) || k <= 0 || !Number.isFinite(maxRoas) || maxRoas <= 0) {
    return null;
  }
  if (maxRoas < targetRoas) {
    return null;
  }
  if (maxRoas === targetRoas) {
    return 0;
  }
  const maxSpend = (k * (maxRoas - targetRoas)) / targetRoas;
  return Number.isFinite(maxSpend) && maxSpend >= 0 ? maxSpend : null;
}

export function resolveLatestCohortMarketingSpend(cohortSheet) {
  const cohorts = cohortSheet?.cohorts ?? [];
  if (!cohorts.length) {
    return null;
  }
  const latestSpend = Number(cohorts[cohorts.length - 1]?.marketingSpend) || 0;
  return latestSpend > 0 ? latestSpend : null;
}

/** @deprecated use resolveLatestCohortMarketingSpend */
export function resolveReferenceMarketingSpend(cohortSheet) {
  return resolveLatestCohortMarketingSpend(cohortSheet);
}

export function computeUaPredictionRatio(referenceSpend, maxMarketingRoas100) {
  const reference = Number(referenceSpend) || 0;
  const maxSpend = Number(maxMarketingRoas100);
  if (reference <= 0 || !Number.isFinite(maxSpend) || maxSpend < 0) {
    return null;
  }
  const ratio = maxSpend / reference;
  return Number.isFinite(ratio) && ratio > 0 ? ratio : null;
}

export function resolveUaPredictionInputs({
  cohortSheet,
  hillModel = null,
  lastMarketingSpend = 0,
  maxMarketingRoas100 = 0,
}) {
  const cohortLatest = resolveLatestCohortMarketingSpend(cohortSheet);
  const referenceSpend =
    Number(lastMarketingSpend) > 0 ? Number(lastMarketingSpend) : cohortLatest;

  const targetMobIndex = resolveMaxRoas100TargetMob(hillModel);

  let maxSpend = Number(maxMarketingRoas100);
  if (!Number.isFinite(maxSpend) || maxSpend <= 0) {
    maxSpend =
      hillModel && referenceSpend && targetMobIndex != null
        ? maxSpendForTargetRoasHill(
            hillModel,
            targetMobIndex,
            UA_PREDICTION_TARGET_ROAS
          )
        : null;
  }

  const ratio = computeUaPredictionRatio(referenceSpend, maxSpend);
  return {
    referenceSpend,
    maxSpend,
    ratio,
    targetMob: targetMobIndex != null ? targetMobIndex + 1 : null,
    targetRoas: UA_PREDICTION_TARGET_ROAS,
    solvable: maxSpend != null,
  };
}

export function computeUaPredictionFromHill(hillModel, referenceSpend) {
  const targetMobIndex = resolveMaxRoas100TargetMob(hillModel);
  if (targetMobIndex == null) {
    return null;
  }
  const reference = Number(referenceSpend) || 0;
  const maxSpend = maxSpendForTargetRoasHill(
    hillModel,
    targetMobIndex,
    UA_PREDICTION_TARGET_ROAS
  );
  const result = {
    ratio: null,
    maxSpend,
    referenceSpend: reference,
    targetMob: targetMobIndex + 1,
    targetRoas: UA_PREDICTION_TARGET_ROAS,
    solvable: maxSpend != null,
  };
  if (maxSpend != null && reference > 0) {
    const ratio = maxSpend / reference;
    if (Number.isFinite(ratio) && ratio > 0) {
      result.ratio = ratio;
    }
  }
  return result;
}

export function formatUaPredictionRatio(ratio) {
  if (ratio == null || !Number.isFinite(Number(ratio))) {
    return null;
  }
  return `${Number(ratio).toFixed(2)}x`;
}
